import { Container, Graphics, Text } from 'pixi.js';
import gsap from 'gsap';
import {
  spawnShockwave,
  spawnConfetti,
  spawnSunburst,
  spawnStompImpact,
} from './WinFx';

/**
 * Endgame Finishers — the equippable "kill" effect that plays at MATCH_OVER.
 * Each finisher is a pure function over {@link FinisherCtx}; it owns everything
 * that happens FROM the stomp-impact frame (the winner bell + sunburst + stomp
 * windup are the shared intro in MatchScene). The map is keyed by `fxKey`, the
 * value stored on the equipped `endgameFx` cosmetic's `data.fxKey`.
 *
 * Everything here is built from cheap Pixi Graphics + GSAP tweens that
 * self-destruct, so finishers add no asset weight and can't leak nodes.
 */
export interface FinisherCtx {
  /** Additive-blend FX layer (inside the shaking root). */
  fx: Container;
  /** Winner climber world position. */
  winner: { x: number; y: number };
  /** Loser climber world position. */
  loser: { x: number; y: number };
  width: number;
  height: number;
  /** Clamped stompPower (0.5–2.5) — scales intensity. */
  power: number;
  shake: (intensity: number, duration: number) => void;
  hitStop: (ms: number) => void;
  /** Slow the whole game to `scale` for `ms`, then restore. */
  slowMo: (scale: number, ms: number) => void;
  /** Full-screen colour flash (stage overlay), peak alpha then fade. */
  flash: (color: number, peak: number, durSec: number) => void;
  /** Knock the loser down the wall (the universal "you lost" beat). */
  knockdownLoser: () => void;
}

export type Finisher = (ctx: FinisherCtx) => void;

const GOLD = 0xf4c724;
const WHITE = 0xffffff;
const CYAN = 0x22d3ee;

// ─── shared local FX helpers ─────────────────────────────────────────────────

/** Jagged lightning bolt from the top of the screen down to (x,y). */
function boltStrike(fx: Container, x: number, topY: number, y: number, color = WHITE): void {
  const g = new Graphics();
  fx.addChild(g);
  const segs = 8;
  let px = x;
  let py = topY;
  g.moveTo(px, py);
  for (let i = 1; i <= segs; i++) {
    const t = i / segs;
    const nx = x + (Math.random() - 0.5) * 46 * (1 - t);
    const ny = topY + (y - topY) * t;
    g.lineTo(nx, ny);
    px = nx;
    py = ny;
  }
  g.stroke({ width: 6, color, alpha: 1 });
  g.stroke({ width: 14, color, alpha: 0.25 });
  gsap.to(g, { alpha: 0, duration: 0.4, delay: 0.08, onComplete: () => g.destroy() });
}

/** Gold coins fountaining up from (x,y) then raining down. */
function coinFountain(fx: Container, x: number, y: number, count: number): void {
  for (let i = 0; i < count; i++) {
    const coin = new Graphics();
    coin.circle(0, 0, 6 + Math.random() * 3).fill({ color: GOLD });
    coin.circle(0, 0, 3).stroke({ width: 1.5, color: 0xfff3b0, alpha: 0.9 });
    coin.x = x;
    coin.y = y;
    fx.addChild(coin);
    const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
    const spd = 120 + Math.random() * 200;
    const dur = 1.0 + Math.random() * 0.6;
    gsap.to(coin, {
      x: x + Math.cos(ang) * spd,
      y: y + Math.sin(ang) * spd + 320,
      duration: dur,
      ease: 'power1.out',
    });
    gsap.to(coin.scale, { x: 0.4, y: 1, duration: 0.18, yoyo: true, repeat: Math.round(dur * 6), ease: 'sine.inOut' });
    gsap.to(coin, { alpha: 0, duration: 0.35, delay: dur - 0.35, onComplete: () => coin.destroy() });
  }
}

/** Frozen prism over the loser that cracks and bursts into shards. */
function iceShatter(fx: Container, x: number, y: number): void {
  const ice = new Graphics();
  ice.roundRect(-26, -42, 52, 84, 8).fill({ color: 0xbfeeff, alpha: 0.55 });
  ice.roundRect(-26, -42, 52, 84, 8).stroke({ width: 2, color: WHITE, alpha: 0.8 });
  ice.x = x;
  ice.y = y;
  fx.addChild(ice);
  gsap.to(ice.scale, { x: 1.05, y: 1.05, duration: 0.22, ease: 'power2.out' });
  gsap.to(ice, {
    alpha: 0,
    duration: 0.2,
    delay: 0.26,
    onComplete: () => ice.destroy(),
  });
  // Shards flying outward on shatter.
  window.setTimeout(() => {
    for (let i = 0; i < 16; i++) {
      const s = new Graphics();
      const r = 5 + Math.random() * 7;
      s.poly([0, -r, r * 0.6, r, -r * 0.6, r]).fill({ color: i % 2 ? 0xbfeeff : WHITE, alpha: 0.9 });
      s.x = x;
      s.y = y;
      fx.addChild(s);
      const a = Math.random() * Math.PI * 2;
      const d = 60 + Math.random() * 140;
      gsap.to(s, {
        x: x + Math.cos(a) * d,
        y: y + Math.sin(a) * d + 80,
        rotation: Math.random() * 8,
        alpha: 0,
        duration: 0.6 + Math.random() * 0.3,
        ease: 'power2.out',
        onComplete: () => s.destroy(),
      });
    }
    spawnShockwave(fx, x, y, 0xbfeeff, 90);
  }, 240);
}

/** A flaming meteor falling from off-screen top to the target, then onImpact. */
function meteorDrop(
  fx: Container,
  toX: number,
  toY: number,
  onImpact: () => void,
): void {
  const m = new Container();
  const head = new Graphics();
  head.circle(0, 0, 20).fill({ color: 0xff7a18 });
  head.circle(0, 0, 11).fill({ color: GOLD });
  m.addChild(head);
  m.x = toX - 220;
  m.y = -120;
  fx.addChild(m);
  // Trailing embers.
  const trailTimer = window.setInterval(() => {
    if (m.destroyed) return;
    const e = new Graphics();
    e.circle(0, 0, 4 + Math.random() * 5).fill({ color: Math.random() > 0.5 ? 0xff7a18 : GOLD, alpha: 0.8 });
    e.x = m.x;
    e.y = m.y;
    fx.addChildAt(e, 0);
    gsap.to(e, { alpha: 0, duration: 0.4, onComplete: () => e.destroy() });
  }, 16);
  gsap.to(m, {
    x: toX,
    y: toY,
    duration: 0.42,
    ease: 'power2.in',
    onComplete: () => {
      window.clearInterval(trailTimer);
      m.destroy();
      onImpact();
    },
  });
}

/** Spinning spiral that shrinks into a singularity, then onCollapse. */
function vortexImplode(fx: Container, x: number, y: number, onCollapse: () => void): void {
  const g = new Graphics();
  const arms = 5;
  for (let a = 0; a < arms; a++) {
    const base = (a / arms) * Math.PI * 2;
    for (let r = 10; r < 150; r += 8) {
      const ang = base + r * 0.05;
      g.circle(Math.cos(ang) * r, Math.sin(ang) * r, 3.4).fill({ color: a % 2 ? 0x9b5de5 : CYAN, alpha: 0.8 });
    }
  }
  g.x = x;
  g.y = y;
  fx.addChild(g);
  gsap.to(g, { rotation: 6, duration: 0.6, ease: 'power2.in' });
  gsap.to(g.scale, {
    x: 0.05,
    y: 0.05,
    duration: 0.55,
    ease: 'power3.in',
    onComplete: () => {
      g.destroy();
      onCollapse();
    },
  });
}

/** Big pop-in display text (e.g. "K.O."). */
function bigText(fx: Container, x: number, y: number, label: string, color = WHITE): void {
  const t = new Text({
    text: label,
    style: {
      fontFamily: 'Bungee, Impact, sans-serif',
      fontSize: 96,
      fontWeight: '900',
      fill: color,
      stroke: { color: 0x1e1b4b, width: 8 },
      letterSpacing: 4,
    },
  });
  t.anchor.set(0.5);
  t.x = x;
  t.y = y;
  t.scale.set(0);
  t.rotation = -0.12;
  fx.addChild(t);
  const tl = gsap.timeline({ onComplete: () => t.destroy() });
  tl.to(t.scale, { x: 1, y: 1, duration: 0.22, ease: 'back.out(3)' });
  tl.to(t, { rotation: 0.04, duration: 0.5, ease: 'sine.inOut' }, 0);
  tl.to(t, { alpha: 0, y: y - 28, duration: 0.35, delay: 0.45 });
}

/** Sweeping flame cone toward the loser. */
function dragonFire(fx: Container, fromX: number, fromY: number, toX: number, toY: number): void {
  const dir = Math.atan2(toY - fromY, toX - fromX);
  for (let i = 0; i < 26; i++) {
    const flame = new Graphics();
    const r = 10 + Math.random() * 16;
    const col = [0xff3b18, 0xff7a18, GOLD][Math.floor(Math.random() * 3)]!;
    flame.circle(0, 0, r).fill({ color: col, alpha: 0.85 });
    flame.x = fromX;
    flame.y = fromY;
    fx.addChild(flame);
    const spread = (Math.random() - 0.5) * 0.6;
    const d = 120 + Math.random() * 260;
    const delay = Math.random() * 0.25;
    gsap.to(flame, {
      x: fromX + Math.cos(dir + spread) * d,
      y: fromY + Math.sin(dir + spread) * d,
      duration: 0.5 + Math.random() * 0.3,
      delay,
      ease: 'power1.out',
    });
    gsap.to(flame.scale, { x: 1.8, y: 1.8, duration: 0.5, delay });
    gsap.to(flame, { alpha: 0, duration: 0.4, delay: delay + 0.3, onComplete: () => flame.destroy() });
  }
}

/** Twinkling starfield over the whole screen (galaxy finisher). */
function starfield(fx: Container, w: number, h: number, count = 70): void {
  for (let i = 0; i < count; i++) {
    const s = new Graphics();
    const r = 1 + Math.random() * 2.5;
    s.circle(0, 0, r).fill({ color: Math.random() > 0.6 ? CYAN : WHITE });
    s.x = Math.random() * w;
    s.y = Math.random() * h;
    s.alpha = 0;
    fx.addChild(s);
    gsap.to(s, {
      alpha: 0.9,
      duration: 0.3 + Math.random() * 0.4,
      yoyo: true,
      repeat: 1,
      delay: Math.random() * 0.5,
      onComplete: () => s.destroy(),
    });
  }
}

/** Big jagged ground/wall cracks radiating from (x,y). */
function groundCracks(fx: Container, x: number, y: number, scale = 1): void {
  const crack = new Graphics();
  crack.x = x;
  crack.y = y;
  fx.addChild(crack);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    let cx = 0;
    let cy = 0;
    crack.moveTo(0, 0);
    const steps = 4;
    for (let s = 1; s <= steps; s++) {
      const len = (30 + Math.random() * 40) * scale;
      cx += Math.cos(a + (Math.random() - 0.5) * 0.5) * len;
      cy += Math.sin(a + (Math.random() - 0.5) * 0.5) * len * 0.55;
      crack.lineTo(cx, cy);
    }
    crack.stroke({ width: 3, color: 0x1a1340, alpha: 0.9 });
  }
  gsap.to(crack, { alpha: 0, duration: 0.9, delay: 0.3, onComplete: () => crack.destroy() });
}

// ─── finishers ───────────────────────────────────────────────────────────────

/** The classic stomp — the ALWAYS-ON base finish, also the fallback for any
 * unknown fxKey. Faithful to the original win sequence. */
const defaultFinisher: Finisher = (c) => {
  c.hitStop(90);
  c.shake(10 * c.power, 0.45 + 0.1 * c.power);
  spawnShockwave(c.fx, c.winner.x, c.winner.y, GOLD, 110 * c.power);
  spawnStompImpact(c.fx, c.loser.x, c.loser.y);
  spawnConfetti(c.fx, c.winner.x, c.winner.y - 80, Math.round(26 * c.power));
  c.knockdownLoser();
  spawnConfetti(c.fx, c.width / 2, 40, Math.round(20 * c.power));
  // Legendary-tier flourish: a second white shockwave + top sunburst.
  if (c.power >= 1.8) {
    window.setTimeout(() => {
      spawnShockwave(c.fx, c.winner.x, c.winner.y, WHITE, 160 * c.power);
      spawnSunburst(c.fx, c.winner.x, c.winner.y - 40);
    }, 140);
  }
};

export const FINISHERS: Record<string, Finisher> = {
  default: defaultFinisher,

  confetti_storm: (c) => {
    c.hitStop(70);
    c.shake(7 * c.power, 0.4);
    spawnShockwave(c.fx, c.winner.x, c.winner.y, GOLD, 100 * c.power);
    spawnConfetti(c.fx, c.winner.x, c.winner.y - 80, Math.round(40 * c.power));
    spawnConfetti(c.fx, c.width * 0.25, 30, Math.round(34 * c.power));
    spawnConfetti(c.fx, c.width * 0.75, 30, Math.round(34 * c.power));
    spawnConfetti(c.fx, c.loser.x, c.loser.y - 40, Math.round(26 * c.power));
    c.knockdownLoser();
  },

  coin_rain: (c) => {
    c.hitStop(80);
    c.shake(8 * c.power, 0.42);
    spawnShockwave(c.fx, c.winner.x, c.winner.y, GOLD, 110 * c.power);
    coinFountain(c.fx, c.winner.x, c.winner.y - 30, Math.round(36 * c.power));
    coinFountain(c.fx, c.width / 2, 20, Math.round(30 * c.power));
    spawnStompImpact(c.fx, c.loser.x, c.loser.y);
    c.knockdownLoser();
  },

  lightning: (c) => {
    c.flash(WHITE, 0.55, 0.4);
    c.hitStop(110);
    c.shake(13 * c.power, 0.5);
    boltStrike(c.fx, c.loser.x, -40, c.loser.y, WHITE);
    boltStrike(c.fx, c.loser.x - 18, -40, c.loser.y, CYAN);
    spawnShockwave(c.fx, c.loser.x, c.loser.y, CYAN, 130 * c.power);
    spawnStompImpact(c.fx, c.loser.x, c.loser.y);
    c.knockdownLoser();
  },

  ice_shatter: (c) => {
    c.hitStop(120);
    c.flash(0xbfeeff, 0.3, 0.45);
    iceShatter(c.fx, c.loser.x, c.loser.y);
    window.setTimeout(() => {
      c.shake(11 * c.power, 0.45);
      c.knockdownLoser();
    }, 240);
  },

  meteor: (c) => {
    meteorDrop(c.fx, c.loser.x, c.loser.y, () => {
      c.hitStop(120);
      c.flash(0xff7a18, 0.4, 0.4);
      c.shake(16 * c.power, 0.6);
      spawnShockwave(c.fx, c.loser.x, c.loser.y, 0xff7a18, 170 * c.power);
      spawnStompImpact(c.fx, c.loser.x, c.loser.y);
      spawnConfetti(c.fx, c.loser.x, c.loser.y - 40, Math.round(22 * c.power));
      c.knockdownLoser();
    });
  },

  black_hole: (c) => {
    c.slowMo(0.35, 600);
    vortexImplode(c.fx, c.loser.x, c.loser.y, () => {
      c.hitStop(110);
      c.flash(0x9b5de5, 0.4, 0.4);
      c.shake(14 * c.power, 0.5);
      spawnShockwave(c.fx, c.loser.x, c.loser.y, 0x9b5de5, 150 * c.power);
      spawnShockwave(c.fx, c.loser.x, c.loser.y, WHITE, 90);
      c.knockdownLoser();
    });
  },

  ko_flash: (c) => {
    c.slowMo(0.25, 700);
    c.flash(WHITE, 0.6, 0.3);
    c.hitStop(140);
    c.shake(12 * c.power, 0.5);
    spawnStompImpact(c.fx, c.loser.x, c.loser.y);
    spawnSunburst(c.fx, c.width / 2, c.height / 2 - 20);
    bigText(c.fx, c.width / 2, c.height / 2 - 20, 'K.O.', GOLD);
    c.knockdownLoser();
  },

  dragon_breath: (c) => {
    c.hitStop(90);
    c.flash(0xff3b18, 0.3, 0.5);
    dragonFire(c.fx, c.winner.x, c.winner.y - 20, c.loser.x, c.loser.y);
    window.setTimeout(() => {
      c.shake(14 * c.power, 0.55);
      spawnShockwave(c.fx, c.loser.x, c.loser.y, 0xff7a18, 140 * c.power);
      spawnStompImpact(c.fx, c.loser.x, c.loser.y);
      c.knockdownLoser();
    }, 180);
  },

  galaxy: (c) => {
    c.slowMo(0.3, 900);
    starfield(c.fx, c.width, c.height);
    spawnSunburst(c.fx, c.winner.x, c.winner.y - 30);
    c.hitStop(120);
    c.flash(0x9b5de5, 0.35, 0.6);
    c.shake(13 * c.power, 0.6);
    spawnShockwave(c.fx, c.loser.x, c.loser.y, CYAN, 150 * c.power);
    window.setTimeout(() => {
      spawnShockwave(c.fx, c.winner.x, c.winner.y, WHITE, 180 * c.power);
      spawnSunburst(c.fx, c.width / 2, c.height / 2);
    }, 160);
    c.knockdownLoser();
  },

  earthquake: (c) => {
    c.hitStop(110);
    c.shake(20 * c.power, 0.9);
    c.flash(0x8a6a00, 0.25, 0.5);
    groundCracks(c.fx, c.loser.x, c.loser.y, c.power);
    groundCracks(c.fx, c.width / 2, c.height - 30, c.power * 1.4);
    spawnShockwave(c.fx, c.loser.x, c.loser.y, GOLD, 150 * c.power);
    spawnStompImpact(c.fx, c.loser.x, c.loser.y);
    // Falling rubble.
    for (let i = 0; i < Math.round(14 * c.power); i++) {
      const rock = new Graphics();
      const s = 6 + Math.random() * 12;
      rock.rect(-s / 2, -s / 2, s, s).fill({ color: 0x3a2f5a });
      rock.x = Math.random() * c.width;
      rock.y = -20;
      rock.rotation = Math.random() * Math.PI;
      c.fx.addChild(rock);
      gsap.to(rock, {
        y: c.height + 40,
        rotation: rock.rotation + (Math.random() - 0.5) * 6,
        duration: 0.8 + Math.random() * 0.5,
        delay: Math.random() * 0.2,
        ease: 'power1.in',
        onComplete: () => rock.destroy(),
      });
    }
    c.knockdownLoser();
  },
};

/** Resolve a finisher by key, falling back to the default stomp. */
export function getFinisher(fxKey?: string | null): Finisher {
  return (fxKey && FINISHERS[fxKey]) || FINISHERS.default!;
}

/** Catalog metadata for shop/inventory previews (emoji + accent colour). */
export const FINISHER_META: Record<string, { emoji: string; color: string }> = {
  default: { emoji: '🥾', color: '#F4C724' },
  confetti_storm: { emoji: '🎉', color: '#F4C724' },
  coin_rain: { emoji: '💰', color: '#F4C724' },
  lightning: { emoji: '⚡', color: '#22D3EE' },
  ice_shatter: { emoji: '❄️', color: '#BFEEFF' },
  meteor: { emoji: '☄️', color: '#FF7A18' },
  black_hole: { emoji: '🕳️', color: '#9B5DE5' },
  ko_flash: { emoji: '🥊', color: '#F4C724' },
  dragon_breath: { emoji: '🐉', color: '#FF3B18' },
  galaxy: { emoji: '🌌', color: '#9B5DE5' },
  earthquake: { emoji: '🌋', color: '#E63946' },
};
